import { useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { AlertTriangle, Bell, BellOff, CheckCheck, CheckCircle2, Info, ShieldAlert, ShieldCheck, Trophy } from 'lucide-react';
import { Badge, Card, EmptyState, ErrorState, LoadingState, SectionHeader, cn } from '@pixlide/ui';
import { formatRelative } from '@/lib/format';
import {
  useMarkAllNotificationsRead,
  useMarkNotificationRead,
  useNotifications,
} from '@/api/queries/dashboard';
import type { components } from '@/api/generated/schema';

type NotificationListItem = components['schemas']['PixlideApplicationFeaturesNotificationsNotificationListItem'];

type Presentation = { icon: typeof Info; tone: 'neutral' | 'brand' | 'success' | 'warning' | 'danger'; label: string };

/**
 * Sévérité numérique (0-3, voir `NotificationSeverity` côté domaine) traduite en présentation.
 * L'icône ET le libellé portent l'information : la couleur seule n'est pas accessible.
 */
const SEVERITY_PRESENTATION: Record<NotificationListItem['severity'], Presentation> = {
  0: { icon: Info, tone: 'neutral', label: 'Info' },
  1: { icon: CheckCircle2, tone: 'success', label: 'Succès' },
  2: { icon: AlertTriangle, tone: 'warning', label: 'Attention' },
  3: { icon: ShieldAlert, tone: 'danger', label: 'Action requise' },
};

/**
 * Présentation par `type` métier : prioritaire sur la sévérité générique quand elle existe.
 * Un exploit débloqué (voir `GrantFirstSubscriptionExploitAsync` côté API) partage le canal
 * de notification mais reste une récompense, pas un simple message de succès — le trophée et
 * le libellé dédié évitent qu'il se noie parmi les notifications ordinaires.
 */
const TYPE_PRESENTATION: Record<string, Presentation> = {
  'exploit.unlocked': { icon: Trophy, tone: 'brand', label: 'Exploit débloqué' },
  'security.two_factor_enabled': { icon: ShieldCheck, tone: 'success', label: 'Sécurité' },
  'security.two_factor_disabled': { icon: ShieldAlert, tone: 'warning', label: 'Sécurité' },
};

function NotificationRow({ notification }: { notification: NotificationListItem }) {
  const navigate = useNavigate();
  const markRead = useMarkNotificationRead();
  const severity = TYPE_PRESENTATION[notification.type] ?? SEVERITY_PRESENTATION[notification.severity];
  const SeverityIcon = severity.icon;

  function handleActivate() {
    // Marquer comme lue AVANT de naviguer : si la navigation échoue ou est annulée, la
    // notification ne doit pas rester visible comme non lue indéfiniment pour autant — c'est
    // un geste de lecture, pas une confirmation d'action.
    if (!notification.isRead) {
      markRead.mutate(notification.id);
    }

    if (notification.actionUrl) {
      void navigate({ href: notification.actionUrl });
    }
  }

  const interactive = !notification.isRead || Boolean(notification.actionUrl);

  return (
    <Card
      raised={!notification.isRead}
      className={cn(
        'flex items-start gap-4 p-5 transition',
        interactive && 'cursor-pointer hover:shadow-raised',
        !notification.isRead && 'ring-1 ring-brand-100',
      )}
      role={interactive ? 'button' : undefined}
      tabIndex={interactive ? 0 : undefined}
      onClick={interactive ? handleActivate : undefined}
      onKeyDown={
        interactive
          ? (event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                handleActivate();
              }
            }
          : undefined
      }
    >
      <span
        aria-hidden
        className={cn(
          'grid size-10 shrink-0 place-items-center rounded-xl',
          severity.tone === 'neutral' && 'bg-ink-50 text-ink-500',
          severity.tone === 'brand' && 'bg-brand-100 text-brand-700',
          severity.tone === 'success' && 'bg-success-50 text-success-500',
          severity.tone === 'warning' && 'bg-warning-50 text-warning-500',
          severity.tone === 'danger' && 'bg-danger-50 text-danger-500',
        )}
      >
        <SeverityIcon className="size-5" />
      </span>

      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <div className="flex items-start justify-between gap-3">
          <p className="text-sm font-semibold text-ink-900">{notification.title}</p>
          <span className="shrink-0 text-xs text-ink-300">{formatRelative(notification.createdAt)}</span>
        </div>

        <p className="text-sm text-ink-500">{notification.body}</p>

        <div className="mt-1 flex items-center gap-2">
          <Badge tone={severity.tone}>{severity.label}</Badge>
          {!notification.isRead ? (
            <span aria-hidden className="size-1.5 rounded-full bg-brand-500" title="Non lue" />
          ) : null}
        </div>
      </div>
    </Card>
  );
}

export function NotificationsPage() {
  const [onlyUnread, setOnlyUnread] = useState(false);
  const notifications = useNotifications({ onlyUnread });
  const markAllRead = useMarkAllNotificationsRead();

  const unreadCount = notifications.data?.unreadCount ?? 0;
  const items = notifications.data?.page.items ?? [];

  return (
    <div className="mx-auto flex max-w-3xl flex-col gap-6">
      <SectionHeader
        title="Notifications"
        description={
          unreadCount > 0
            ? `${unreadCount} notification${unreadCount > 1 ? 's' : ''} non lue${unreadCount > 1 ? 's' : ''}`
            : 'Vous êtes à jour.'
        }
        action={
          unreadCount > 0 ? (
            <button
              type="button"
              onClick={() => markAllRead.mutate()}
              disabled={markAllRead.isPending}
              className="inline-flex items-center gap-2 rounded-pill bg-ink-50 px-4 py-2 text-sm font-medium text-ink-700 transition hover:bg-ink-100 disabled:opacity-60"
            >
              <CheckCheck aria-hidden className="size-4" />
              Tout marquer comme lu
            </button>
          ) : undefined
        }
      />

      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => setOnlyUnread(false)}
          className={cn(
            'rounded-pill px-3 py-1.5 text-sm font-medium transition',
            !onlyUnread ? 'bg-ink-900 text-white' : 'bg-ink-50 text-ink-500 hover:bg-ink-100',
          )}
        >
          Toutes
        </button>
        <button
          type="button"
          onClick={() => setOnlyUnread(true)}
          className={cn(
            'rounded-pill px-3 py-1.5 text-sm font-medium transition',
            onlyUnread ? 'bg-ink-900 text-white' : 'bg-ink-50 text-ink-500 hover:bg-ink-100',
          )}
        >
          Non lues
        </button>
      </div>

      {notifications.isPending ? <LoadingState /> : null}

      {notifications.isError ? (
        <ErrorState
          message="Impossible de charger vos notifications pour le moment."
          onRetry={() => void notifications.refetch()}
        />
      ) : null}

      {notifications.isSuccess && items.length === 0 ? (
        <EmptyState
          icon={onlyUnread ? <BellOff className="size-10" strokeWidth={1.25} /> : <Bell className="size-10" strokeWidth={1.25} />}
          title={onlyUnread ? 'Aucune notification non lue' : 'Aucune notification'}
          description={
            onlyUnread
              ? 'Toutes vos notifications ont été lues.'
              : 'Les événements concernant votre compte (paiements, écrans, support…) apparaîtront ici.'
          }
        />
      ) : null}

      {items.length > 0 ? (
        <div className="flex flex-col gap-3">
          {items.map((notification) => (
            <NotificationRow key={notification.id} notification={notification} />
          ))}
        </div>
      ) : null}
    </div>
  );
}
